import { FC, useEffect, useMemo } from 'react';

import styled from '@emotion/styled';
import { ChildCollectionEnum, calculateAge } from 'core';
import {
  useGetChildCollectionOptionsQuery,
  useLazyGetSpecificChildCollectionOptionsQuery,
} from 'store/child-collection-option/childCollectionOptionAPI';

import { Button, IcoCross } from '@britishcouncil/react-solas';
import { Divider, ExternalLink, RadioGroup, TnCCheckbox } from '@britishcouncil/react-solas-ors3';
import { FormFields, Radio } from '@britishcouncil/react-forms';

import { ChildDetails, DEFAULT_GUARDIAN, Guardian } from './types';
import { GuardianFields } from './GuardianFields';

var MAX_NUMBER_OF_GUARDIANS = 5;

export const ChildProtection: FC<{
  fields: FormFields<ChildDetails>;
  dateOfBirth: string | Date | undefined;
  onGuardianAdd: (guardian: Guardian) => void;
  onGuardianRemove: (index: number) => void;
}> = ({ fields, dateOfBirth, onGuardianAdd, onGuardianRemove }) => {
  const { data: childCollectionOptions, isSuccess: optionsLoaded } = useGetChildCollectionOptionsQuery();
  const [getSpecificOption, { data: nonEditableChildCollectionOption }] =
    useLazyGetSpecificChildCollectionOptionsQuery();

  const childAge = useMemo(() => calculateAge(dateOfBirth), [dateOfBirth]);

  const childCollectionRadios = useMemo(
    () =>
      childCollectionOptions
        ?.filter((x) => childAge >= x.requiredMinimumAge && childAge <= x.requiredMaximumAge)
        .map((x) => ({ checkValue: x.id, label: x.text })) || [],
    [childCollectionOptions, childAge]
  );

  const AddGuardian = () => {
    onGuardianAdd(DEFAULT_GUARDIAN);
  };

  const RemoveGuardian = (index: number) => {
    onGuardianRemove(index);
  };

  useEffect(() => {
    if (
      optionsLoaded &&
      !!fields.childCollectionOption.value &&
      !childCollectionOptions?.find((x) => x.id === fields.childCollectionOption.value)
    ) {
      getSpecificOption(fields.childCollectionOption.value);
    }
  }, [optionsLoaded, childCollectionOptions, fields.childCollectionOption.value, getSpecificOption]);

  return (
    <>
      <h2>Child Protection Information</h2>
      <p>Are you happy for your child to leave the exam premises unaccompanied at the end of the test?</p>
      <p>
        You can read more about our{' '}
        <ExternalLink
          href="https://www.britishcouncil.org/organisation/transparency/policies/child-protection"
          showIcon
        >
          Child Protection Policy
        </ExternalLink>
      </p>

      {!!nonEditableChildCollectionOption && (
        <div className="radio">
          <Radio {...fields.childCollectionOption} checkValue={nonEditableChildCollectionOption.id} disabled />
          <label>{nonEditableChildCollectionOption.text}</label>
        </div>
      )}

      <RadioGroup {...fields.childCollectionOption} radios={childCollectionRadios} />
      {fields.childCollectionOption.value === ChildCollectionEnum.ParentPickup && (
        <>
          {fields.allowedPeople.map((guardianFields, i) => (
            <GuardianBox key={i}>
              {fields.allowedPeople.length > 1 && <IcoCross onClick={() => RemoveGuardian(i)} />}
              <GuardianFields fields={guardianFields} />
              {fields.allowedPeople.length - 1 !== i && <Divider />}
            </GuardianBox>
          ))}
          {MAX_NUMBER_OF_GUARDIANS > fields.allowedPeople.length && (
            <AddGuardianBtn intent="default" size="xs" onClick={AddGuardian} type="button">
              Add another person
            </AddGuardianBtn>
          )}
          <p>
            Do you confirm that the person(s) listed are authorised to collect child at the end of the test, and they
            are over 14 years old?
          </p>
          <TnCCheckbox {...fields.childStatementConfirmed}>Yes, I confirm</TnCCheckbox>
        </>
      )}
    </>
  );
};

const GuardianBox = styled.div`
  position: relative;
  padding-top: 30px;
  svg {
    fill: red;
    width: 30px;
    position: absolute;
    top: 0;
    right: 0;
    cursor: pointer;
  }
`;

const AddGuardianBtn = styled(Button)`
  margin-bottom: 30px;
`;
